import { useNavigate } from "react-router-dom";
import { Lock, type LucideIcon } from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import { hasOperatorAccess, isFreeBuyerUser } from "@/lib/access";

// Brand colours (match ListingDetail's locked cards)
const NAVY = "var(--color-primary-foreground)"; // ink on the gold CTA
const GOLD = "#E8A020";

/**
 * Full-page access gate showing a locked panel + Upgrade CTA that leads to
 * /choose-plan; everyone with access sees the page content. Wrap a route's page:
 *
 *   <MembersOnlyGate title="Coming Soon Deals" icon={Sparkles}
 *     description="Early access to off-market deals is a members-only privilege.">
 *     <ComingSoonPage />
 *   </MembersOnlyGate>
 *
 * `requires`:
 *  - "member" (default) — locked only for free/Basic buyers (any paid plan unlocks).
 *  - "operator" — locked for Basic AND Explorer; only Operator buyers (+ staff)
 *    unlock. Use for Operator-only features (Deal pipeline CRM, …).
 */
export default function MembersOnlyGate({
  title,
  description,
  icon: Icon = Lock,
  requires = "member",
  children,
}: {
  title: string;
  description: string;
  icon?: LucideIcon;
  requires?: "member" | "operator";
  children: React.ReactNode;
}) {
  const { user } = useAuth();
  const navigate = useNavigate();

  const locked = requires === "operator" ? !hasOperatorAccess(user) : isFreeBuyerUser(user);
  if (!locked) return <>{children}</>;

  const eyebrow = requires === "operator" ? "Operator plan" : "Members only";
  const footerNote = requires === "operator" ? "Included in the Operator plan." : "Included in every paid plan.";

  return (
    <div className="flex flex-1 items-center justify-center px-4 py-16">
      <div className="w-full max-w-lg rounded-2xl border border-gray-100 bg-white p-8 text-center shadow-sm">
        <div
          className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl"
          style={{ backgroundColor: "#FDF3E0", border: `1px solid ${GOLD}55` }}
        >
          <Icon className="h-7 w-7" style={{ color: "#B45309" }} />
        </div>
        <h1 className="mt-4 text-xl font-extrabold text-gray-900">{title}</h1>
        {/* GOLD, not dark amber — this sits on the card surface, which is dark in dark mode. */}
        <p className="mt-1 text-[0.7rem] font-bold uppercase tracking-widest" style={{ color: GOLD }}>
          {eyebrow}
        </p>
        <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-gray-500">{description}</p>
        <button
          onClick={() => navigate("/choose-plan")}
          className="mt-6 inline-flex items-center justify-center gap-1.5 rounded-xl px-8 py-3 text-sm font-extrabold shadow-md transition-opacity hover:opacity-90"
          style={{ backgroundColor: GOLD, color: NAVY }}
        >
          Upgrade to unlock →
        </button>
        <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-gray-400">
          <Lock className="h-3.5 w-3.5" />
          {footerNote}
        </p>
      </div>
    </div>
  );
}
